import { useState } from 'react';

import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Alert, Box, IconButton, Snackbar, Tab, Tabs, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { ErrorState, PageContainer } from '@/shared/ui';

import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { ChannelsPanel } from '../components/ChannelsPanel';
import { PriceApprovalsPanel } from '../components/PriceApprovalsPanel';
import { PriceAuditPanel } from '../components/PriceAuditPanel';
import { PriceGridPanel } from '../components/PriceGridPanel';
import { PriceProtectionPanel } from '../components/PriceProtectionPanel';
import { PricingConfigPanel } from '../components/PricingConfigPanel';
import { PricingRulesPanel } from '../components/PricingRulesPanel';

type TabKey = 'grid' | 'rules' | 'config' | 'approvals' | 'protection' | 'audit' | 'channels';

/** Price governance — rules, configuration, approval queue, protection policy and audit. */
export function PricingPage() {
  const { canPricingView, canPricingManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();
  const [tab, setTab] = useState<TabKey>('grid');

  const onResult = (severity: 'success' | 'error' | 'info' | 'warning', message: string) =>
    severity === 'success' ? success(message) : notifyError(message);

  if (!canPricingView) {
    return (
      <PageContainer title="Цены и правила">
        <ErrorState
          title="Доступ ограничен"
          message="У вас нет прав на управление ценами. Обратитесь к администратору компании."
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Цены и правила"
      subtitle="Управление ценообразованием: правила, конфигурация, утверждения и защита цен."
      actions={
        <Tooltip title="Настройки ценообразования">
          <IconButton
            component={RouterLink}
            to="/settings/pricing"
            aria-label="Настройки ценообразования"
            sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}
          >
            <SettingsRoundedIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={tab} onChange={(_e, value: TabKey) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="grid" label="Сетка цен" />
          <Tab value="rules" label="Правила" />
          <Tab value="config" label="Конфигурация" />
          <Tab value="approvals" label="Утверждения" />
          <Tab value="protection" label="Защита цен" />
          <Tab value="audit" label="Аудит" />
          <Tab value="channels" label="Каналы" />
        </Tabs>
      </Box>

      {tab === 'grid' ? <PriceGridPanel /> : null}
      {tab === 'rules' ? <PricingRulesPanel canManage={canPricingManage} onResult={onResult} /> : null}
      {tab === 'config' ? <PricingConfigPanel canManage={canPricingManage} onResult={onResult} /> : null}
      {tab === 'approvals' ? (
        <PriceApprovalsPanel canManage={canPricingManage} onResult={onResult} />
      ) : null}
      {tab === 'protection' ? (
        <PriceProtectionPanel canManage={canPricingManage} onResult={onResult} />
      ) : null}
      {tab === 'audit' ? <PriceAuditPanel /> : null}
      {tab === 'channels' ? <ChannelsPanel /> : null}

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
